import type { AssignedClassGame } from '@wx-weiqi/contracts';
import {
  assignedGameStatusLabels,
  filterAssignedClassGames,
  type AssignedGamesView,
} from '../domain/assigned-class-games';
import './assigned-class-games.css';

interface AssignedClassGamesPanelProps {
  readonly games: readonly AssignedClassGame[];
  readonly view: AssignedGamesView;
  readonly busy: boolean;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onViewChange: (view: AssignedGamesView) => void;
  readonly onClose: () => void;
  readonly onOpen: (gameId: string) => void;
  readonly onRefresh?: (() => void) | undefined;
}

export function AssignedClassGamesPanel({
  games,
  view,
  busy,
  loading,
  error,
  onViewChange,
  onClose,
  onOpen,
  onRefresh,
}: AssignedClassGamesPanelProps) {
  const visibleGames = filterAssignedClassGames(games, view);
  return (
    <div className="class-match-panel">
      <header className="class-match-heading">
        <div>
          <small>老师安排的对局</small>
          <h2>班级对弈</h2>
          <p>查看对局日期和状态，准备开局或继续未完成的对局。</p>
        </div>
        <button
          className="dialog-close"
          type="button"
          aria-label="关闭弹窗"
          disabled={busy}
          onClick={onClose}
        >
          ×
        </button>
      </header>
      <div className="class-match-filters">
        <div role="group" aria-label="班级对弈状态筛选">
          {(
            [
              ['CURRENT', '当前对局'],
              ['HISTORY', '历史对局'],
              ['ALL', '全部对局'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              disabled={busy}
              onClick={() => onViewChange(value)}
            >
              {label}
              <span>{filterAssignedClassGames(games, value).length}</span>
            </button>
          ))}
        </div>
        {onRefresh !== undefined ? (
          <button
            className="class-match-refresh"
            type="button"
            disabled={busy || loading}
            onClick={onRefresh}
          >
            {loading ? '刷新中…' : '刷新'}
          </button>
        ) : null}
      </div>
      {error !== null ? (
        <p className="message message--error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="class-match-list" aria-live="polite" aria-busy={loading}>
        {loading ? <p className="class-match-empty">正在读取班级对局…</p> : null}
        {!loading && error === null && visibleGames.length === 0 ? (
          <p className="class-match-empty">
            {view === 'HISTORY'
              ? '暂无历史班级对局。'
              : view === 'ALL'
                ? '暂无班级对局。'
                : '暂无待准备或进行中的班级对局。'}
          </p>
        ) : null}
        {!loading &&
          visibleGames.map((game) => {
            const terminal = game.status === 'FINISHED' || game.status === 'ABORTED';
            const waiting = ['CREATED', 'WAITING', 'READY'].includes(game.status);
            return (
              <article className="class-match" key={game.assignmentId}>
                <header>
                  <strong>{game.className}</strong>
                  <span className="class-match-status" data-status={game.status}>
                    {assignedGameStatusLabels[game.status]}
                  </span>
                </header>
                <div className="class-match-players">
                  {(['BLACK', 'WHITE'] as const).map((color) => (
                    <span key={color}>
                      <i data-color={color} aria-hidden="true" />
                      <small>{color === 'BLACK' ? '黑方' : '白方'}</small>
                      <strong>
                        {game.players.find((player) => player.color === color)?.displayName ?? '—'}
                      </strong>
                    </span>
                  ))}
                </div>
                <footer>
                  <div className="class-match-meta">
                    <span>
                      对局日期{' '}
                      <time dateTime={game.createdAt}>
                        {new Date(game.createdAt).toLocaleString('zh-CN', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                      </time>
                    </span>
                    <small>
                      {game.boardSize} 路棋盘 · 房间 {game.roomCode}
                    </small>
                  </div>
                  <button type="button" disabled={busy} onClick={() => onOpen(game.gameId)}>
                    {terminal ? '查看对局' : waiting ? '进入对局室' : '继续对局'}
                    <span aria-hidden="true"> →</span>
                  </button>
                </footer>
              </article>
            );
          })}
      </div>
    </div>
  );
}
